import React, { useEffect } from "react";
import { useUserContext } from "../../_context/UserContext";
import { redirect } from "react-router-dom";
import { Link } from "react-router-dom";
import "./Admin.css";

/* ── SVG Icons ──────────────────────────────────────────── */
const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const BroomIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 20l-7-7" />
    <path d="M9 3l-.5 9.5L4 18h16l-4.5-5.5L15 3H9z" />
  </svg>
);

const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 9h1m5 0h1M9 13h1m5 0h1M9 17h1m5 0h1" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ── Card definition ─────────────────────────────────────── */
interface AdminCard {
  label: string;
  desc: string;
  toPage: string;
  icon: React.ReactNode;
  iconTheme: "purple" | "gold" | "blue";
}

const ADMIN_CARDS: AdminCard[] = [
  {
    label: "Add System Users",
    desc: "Create new admin or user accounts",
    toPage: "add_system_users",
    icon: <UserPlusIcon />,
    iconTheme: "purple",
  },
  {
    label: "Add New Cleaner",
    desc: "Register a new cleaning staff member",
    toPage: "add_new_cleaner",
    icon: <BroomIcon />,
    iconTheme: "gold",
  },
  {
    label: "Add New Client",
    desc: "Onboard a new client record",
    toPage: "add_new_client",
    icon: <BuildingIcon />,
    iconTheme: "blue",
  },
  {
    label: "View Users",
    desc: "Browse and manage system users",
    toPage: "view_users",
    icon: <UsersIcon />,
    iconTheme: "purple",
  },
  {
    label: "View Cleaners",
    desc: "See all registered cleaners",
    toPage: "view_cleaners",
    icon: <ClipboardIcon />,
    iconTheme: "gold",
  },
  {
    label: "View Clients",
    desc: "Search and browse client records",
    toPage: "view_clients",
    icon: <EyeIcon />,
    iconTheme: "blue",
  },
];

/* ── Component ───────────────────────────────────────────── */
export const Admin = () => {
  const { isLogged, setCurrentPage } = useUserContext();

  useEffect(() => {
    if (!isLogged) {
      redirect("/");
    }
  }, [isLogged]);

  useEffect(() => {
    setCurrentPage("Admin Options");
  }, []);

  return (
    <div>
      {/* Welcome header */}
      <div className="admin-welcome">
        <span className="admin-welcome-eyebrow">CNA Cleaning Services</span>
        <h1 className="admin-welcome-title">Admin Dashboard</h1>
        <p className="admin-welcome-subtitle">
          Manage users, cleaners, and clients from one place.
        </p>
      </div>

      <div className="admin-divider" />

      {/* Action card grid */}
      <div className="admin-card-grid">
        {ADMIN_CARDS.map((card) => (
          <Link
            key={card.toPage}
            to={card.toPage}
            className="admin-action-card"
          >
            <div>
              <div className={`admin-card-icon-wrap ${card.iconTheme}`}>
                {card.icon}
              </div>
              <p className="admin-card-label">{card.label}</p>
              <p className="admin-card-desc">{card.desc}</p>
            </div>
            <div className="admin-card-footer">
              <span className="admin-card-arrow">
                <ArrowRightIcon />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
